import React, { useEffect, useState } from 'react';
import {
  View, Text, StyleSheet, TextInput, ActivityIndicator, Keyboard, KeyboardAvoidingView, Platform,
} from 'react-native';
import Modal from 'react-native-modal';
import Style from '../../../constants/Style';
import Colors from '../../../constants/Colors';
import Button from '../../../components/Button';
import isMobile from '../../../components/IsMobile';
import ResolvedIconMobile from '../../../components/16pxIcons/ResolvedIconMobile';
import ResolvedIcon from '../../../components/ResolvedIcon';
import updateTicketStatus from '../../../requests/Tickets/UpdateTicketStatus';
import { TicketStatus } from '../../../constants/Types';
import { TICKET_STATUS_UPDATE_FAILURE } from '../../../constants/AlertMessages';

const defaultError: string = TICKET_STATUS_UPDATE_FAILURE;
const maxLength: number = 300;

interface ClosureModalProps {
  isVisible: boolean,
  hide: () => void,
  id: string,
  resolve: () => void,
}

const ClosureModal = ({
  isVisible, hide, id, resolve,
}: ClosureModalProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [value, onChangeText] = useState<string>('');
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setError(null), [value]);

  const save: () => void = () => {
    setLoading(true);
    updateTicketStatus(id, TicketStatus.COMPLETED, value?.trim()).then((response) => {
      if (response && !response.isError) {
        resolve();
        setSubmitted(true);
      } else setError(response?.errorText ?? defaultError);
      setLoading(false);
    }).catch((e) => {
      setError(e?.message ?? defaultError);
      setLoading(false);
    });
  };

  const reset: () => void = () => {
    setLoading(false);
    onChangeText('');
    setSubmitted(false);
  };

  return <Modal
    {...{ isVisible }}
    onBackButtonPress={hide}
    onBackdropPress={hide}
    style={Style.modal}
    onModalHide={reset}
  >
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={20}
      testID='closure-note-modal'
    >
      <View style={[Style.modalContent, styles.container]}>
        <View style={styles.iconContainer}>
          {isMobile ? <ResolvedIconMobile /> : <ResolvedIcon />}
        </View>
        <Text style={styles.title}>
          {submitted ? 'Ticket has been resolved!' : 'Tell us how you solved this ticket'}
        </Text>
        {submitted ? <Button
          affirmative
          title='Close'
          onPress={hide}
          style={styles.closeButton}
          testID='close-resolved-modal-btn'
        /> : <>
          <TextInput
            multiline
            onBlur={Keyboard.dismiss}
            scrollEnabled
            style={[styles.text, styles.input]}
            {...{ value, onChangeText, maxLength }}
            placeholder='Please leave a note'
            placeholderTextColor={styles.text.color}
          />
          <Text style={styles.characterLimitLabel}>
            {`${value.length ?? 0}/${maxLength} characters`}
          </Text>
          {error && <Text style={[styles.text, styles.errorText]}>{error}</Text>}
          <View style={[Style.row, styles.buttonContainer]}>
            <Button
              title='Cancel'
              onPress={hide}
            />
            <Button
              affirmative
              title='Confirm'
              onPress={save}
              disabled={loading || !(value?.length > 0)}
              testID='confirm-resolve-ticket-btn'
            />
          </View>
        </>}
        <ActivityIndicator
          size='large'
          style={Style.activityIndicator}
          animating={loading}
        />
      </View>
    </KeyboardAvoidingView>
  </Modal>;
};

const styles = StyleSheet.create({
  container: {
    borderWidth: 1,
    borderColor: Colors.light.hereworksGreen400,
    padding: isMobile ? 24 : 40,
    width: isMobile ? '80%' : 414,
    alignSelf: 'center',
  },
  iconContainer: {
    width: isMobile ? 40 : 64,
    height: isMobile ? 40 : 64,
    borderRadius: 32,
    backgroundColor: Colors.light.hereworksGreen400,
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'center',
  },
  title: {
    marginTop: isMobile ? 20 : 32,
    marginBottom: isMobile ? 36 : 52,
    fontSize: isMobile ? 20 : 28,
    lineHeight: isMobile ? 24 : 34,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksGreen400,
    textAlign: 'center',
    alignSelf: 'center',
  },
  text: {
    fontSize: 16,
    lineHeight: 24,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
    marginBottom: 4,
  },
  input: {
    paddingHorizontal: 8,
    paddingVertical: 10,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue100,
    borderRadius: 5,
    color: Colors.light.hereworksBlack600,
    height: 68,
  },
  buttonContainer: {
    marginTop: isMobile ? 24 : 40,
    justifyContent: 'center',
  },
  closeButton: {
    flex: undefined,
  },
  errorText: {
    color: Colors.light.hereworksRed500,
  },
  characterLimitLabel: {
    fontSize: 12,
    lineHeight: 20,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack300,
  },
});

export default ClosureModal;
